import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import roleDropdownCaret from 'static/images/mvp/Vector.png';
import roleLocationIcon from 'static/images/mvp/gis_location-man.png';
import { getTranslatedUserRoleLabel } from '../../../helpers/userRoleDisplay';
import { I18n } from '../../../helpers/i18n';
import { updateMvpUserType } from 'redux/actions/user.action';
import './NavUserRole.scss';

const ROLE_PATH = '/user/role';

/** @returns {'explorer'|'guide'|'both'|null} */
function getMvpNavRoleType(user) {
  const raw = user?.mvpUserType || user?.type;
  if (raw === 'both') return 'both';
  if (raw === 'explorer' || raw === 'senior') return 'explorer';
  if (raw === 'guide' || raw === 'seniorBuddy' || raw === 'student') return 'guide';
  return null;
}

function youthCommunityIdFromUser(user) {
  const yc = user?.youthCommunity;
  if (!yc) return null;
  if (typeof yc === 'string') return yc;
  if (typeof yc === 'object') return yc._id || yc.value || null;
  return null;
}

function triggerLabel(mvpType) {
  if (mvpType === 'guide') return I18n.t('account_type_selection_senior_buddy');
  return I18n.t('account_type_selection_senior');
}

function NavUserRole({ user, updateMvpUserType }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const rolePageLinkRef = useRef(null);

  const isLoggedIn = !!(user && (user.authenticated || user.accessToken));
  const roleLabel = getTranslatedUserRoleLabel(user);
  const rawMvpType = getMvpNavRoleType(user);
  const mvpType = rawMvpType === 'both' ? 'explorer' : rawMvpType;

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        close();
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const goRolePage = useCallback(() => {
    close();
    rolePageLinkRef.current?.click();
  }, [close]);

  const handleExplorerRow = useCallback(async () => {
    if (mvpType === 'explorer') {
      close();
      return;
    }
    if (rawMvpType === 'guide' || rawMvpType === 'both') {
      setLoading(true);
      try {
        await updateMvpUserType('explorer', null);
        toast.success(I18n.t('nav_role_explorer_mode_active'), {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          theme: 'colored',
          className: 'toast-success-container toast-success-container-after',
        });
        close();
      } catch (err) {
        toast.error(err.message || I18n.t('something_wrong'), {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          theme: 'colored',
          className: 'toast-error-container toast-error-container-after',
        });
      } finally {
        setLoading(false);
      }
      return;
    }
    goRolePage();
  }, [mvpType, rawMvpType, close, goRolePage, updateMvpUserType]);

  const handleGuideRow = useCallback(async () => {
    if (mvpType === 'guide') {
      close();
      return;
    }
    const ycId = youthCommunityIdFromUser(user);
    if (!ycId || ycId === 'N/A') {
      goRolePage();
      return;
    }
    if (rawMvpType === 'explorer' || rawMvpType === 'both') {
      setLoading(true);
      try {
        await updateMvpUserType('guide', ycId);
        toast.success(I18n.t('nav_role_guide_mode_active'), {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          theme: 'colored',
          className: 'toast-success-container toast-success-container-after',
        });
        close();
      } catch (err) {
        toast.error(err.message || I18n.t('something_wrong'), {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          theme: 'colored',
          className: 'toast-error-container toast-error-container-after',
        });
      } finally {
        setLoading(false);
      }
    }
  }, [mvpType, rawMvpType, user, close, goRolePage, updateMvpUserType]);

  if (!isLoggedIn || !roleLabel) return null;

  if (!mvpType) {
    return (
      <li className="nav-user-role-item">
        <Link className="nav-user-role nav-user-role--primary" to={ROLE_PATH} title={roleLabel}>
          <img
            src={roleLocationIcon}
            alt=""
            className="nav-user-role__icon"
            width={16}
            height={16}
            aria-hidden
          />
          <span className="nav-user-role__label">{roleLabel}</span>
          <img
            src={roleDropdownCaret}
            alt=""
            className="nav-user-role__caret"
            width={10}
            height={6}
            aria-hidden
          />
        </Link>
      </li>
    );
  }

  const label = triggerLabel(mvpType);

  const explorerActive = mvpType === 'explorer';
  const guideActive = mvpType === 'guide';
  const showExplorerCheck = explorerActive;
  const showGuideCheck = guideActive;

  const explorerRowLabel = explorerActive
    ? I18n.t('nav_role_you_are_explorer')
    : I18n.t('nav_role_learn_as_explorer');

  const guideRowLabel = guideActive
    ? I18n.t('nav_role_you_are_guide')
    : I18n.t('nav_role_teach_as_guide');

  const roleMenuItems = guideActive
    ? [
        {
          key: 'guide',
          active: guideActive,
          label: guideRowLabel,
          onClick: handleGuideRow,
          showCheck: showGuideCheck,
        },
        {
          key: 'explorer',
          active: explorerActive,
          label: explorerRowLabel,
          onClick: handleExplorerRow,
          showCheck: showExplorerCheck,
        },
      ]
    : [
        {
          key: 'explorer',
          active: explorerActive,
          label: explorerRowLabel,
          onClick: handleExplorerRow,
          showCheck: showExplorerCheck,
        },
        {
          key: 'guide',
          active: guideActive,
          label: guideRowLabel,
          onClick: handleGuideRow,
          showCheck: showGuideCheck,
        },
      ];

  return (
    <li className="nav-user-role-item">
      <div
        className={`nav-user-role-wrap${open ? ' nav-user-role-wrap--open' : ''}`}
        ref={wrapRef}
      >
        <Link
          to={ROLE_PATH}
          innerRef={rolePageLinkRef}
          className="nav-user-role__redirect"
          aria-hidden="true"
          tabIndex={-1}
        />
        <button
          type="button"
          className="nav-user-role nav-user-role--primary"
          title={label}
          aria-expanded={open}
          aria-haspopup="true"
          disabled={loading}
          onClick={() => setOpen((v) => !v)}
        >
          <img
            src={roleLocationIcon}
            alt=""
            className="nav-user-role__icon"
            width={16}
            height={16}
            aria-hidden
          />
          <span className="nav-user-role__label">{label}</span>
          <img
            src={roleDropdownCaret}
            alt=""
            className="nav-user-role__caret"
            width={10}
            height={6}
            aria-hidden
          />
        </button>
        {open ? (
          <div className="nav-user-role-dropdown" role="menu">
            {roleMenuItems.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                className={`nav-user-role-dropdown__row${item.active ? ' nav-user-role-dropdown__row--active' : ''}`}
                onClick={item.onClick}
                disabled={loading}
              >
                <span>{item.label}</span>
                {item.showCheck ? (
                  <FontAwesomeIcon icon={faCheck} className="nav-user-role-dropdown__check" aria-hidden />
                ) : null}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </li>
  );
}

const mapDispatch = { updateMvpUserType };

export default connect(null, mapDispatch)(NavUserRole);
